import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as JsSIP from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { RTCSessionEvent } from 'jssip/lib/UA';
import { api } from '../../lib/api/client.js';
import type { BrowserPhone } from '@arcnet/contracts';

/**
 * The workspace's phone: JsSIP over WSS to the edge (docs/36 §8.1).
 *
 * One hook, because a phone is one thing with one state: it is signed in or it is not, there is a
 * call or there is not. Splitting registration from the call would let the two disagree, and a
 * phone that thinks it is registered while the edge has forgotten it is the failure people
 * describe as "it rang once and then never again".
 *
 * The credential is the session's, not the seat's (API browserphone.ts): minted when the phone is
 * switched on and revoked when it is switched off, so nothing in this page outlives the tab.
 * Sign-out therefore has to happen on the way out as well -- the unload handler below -- or a
 * closed tab would leave a registration nobody can see answering calls in an empty room.
 */

export type PhoneState = 'off' | 'connecting' | 'ready' | 'failed';
export type CallState = 'idle' | 'ringing' | 'calling' | 'answered' | 'held';

export interface PhoneCall {
  /** Who is on the other end, as the network gave them. */
  number: string;
  name: string | null;
  inbound: boolean;
  state: CallState;
  /** When it was answered, for the timer. Null while it is still ringing. */
  answeredAt: number | null;
  muted: boolean;
}

interface Options {
  tenantId: string;
  extensionId: string;
  /** Called when a call ends, so the workspace can refresh what it shows. */
  onEnded?: () => void;
}

/** How the last call ended, for a campaign's outcome (docs/36 D1b). */
export interface CallEnd {
  at: number;
  /** It was answered at some point. */
  answered: boolean;
  /** JsSIP's cause when it did not connect: Busy, No Answer, Unavailable, Rejected, Canceled... */
  cause: string | null;
  /** The campaign attempt it was placed for, if any. */
  attemptId: string | null;
}

export interface BrowserPhoneApi {
  info: BrowserPhone | undefined;
  state: PhoneState;
  error: string | null;
  call: PhoneCall | null;
  lastEnd: CallEnd | null;
  /** Pending and answered calls this session, newest first, for the workspace's own list. */
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** attemptId: a preview campaign's call, named to the switch so its record joins the attempt. */
  dial: (number: string, opts?: { attemptId?: string }) => void;
  answer: () => void;
  hangUp: () => void;
  hold: (on: boolean) => void;
  mute: (on: boolean) => void;
  sendDtmf: (digit: string) => void;
  /** Blind transfer: the call leaves and this phone is free. */
  transfer: (target: string) => void;
}

export function useBrowserPhone({ tenantId, extensionId, onEnded }: Options): BrowserPhoneApi {
  const [info, setInfo] = useState<BrowserPhone | undefined>(undefined);
  const [state, setState] = useState<PhoneState>('off');
  const [error, setError] = useState<string | null>(null);
  const [call, setCall] = useState<PhoneCall | null>(null);

  const ua = useRef<JsSIP.UA | null>(null);
  const session = useRef<RTCSession | null>(null);
  const attemptRef = useRef<string | null>(null);
  const [lastEnd, setLastEnd] = useState<CallEnd | null>(null);
  /** One element for the far end's audio, made once: a new element per call loses the autoplay grant. */
  const audio = useRef<HTMLAudioElement | null>(null);
  const base = `/v1/tenants/${tenantId}/extensions/${extensionId}/browser-phone`;

  useEffect(() => {
    let live = true;
    api.get<BrowserPhone>(base).then((r) => { if (live) setInfo(r); }).catch(() => undefined);
    return () => { live = false; };
  }, [base]);

  if (audio.current === null && typeof document !== 'undefined') {
    audio.current = document.createElement('audio');
    audio.current.autoplay = true;
  }

  /** The far end's track, attached wherever a session produces one. */
  const attachMedia = useCallback((s: RTCSession) => {
    const pc = s.connection;
    const play = (): void => {
      const stream = new MediaStream();
      pc.getReceivers().forEach((r) => { if (r.track) stream.addTrack(r.track); });
      if (audio.current) {
        audio.current.srcObject = stream;
        void audio.current.play().catch(() => undefined);
      }
    };
    pc.addEventListener('track', play);
    play();
  }, []);

  const wire = useCallback((s: RTCSession, inbound: boolean) => {
    session.current = s;
    const other = s.remote_identity;
    setCall({
      number: other.uri.user ?? '', name: other.display_name || null,
      inbound, state: inbound ? 'ringing' : 'calling', answeredAt: null, muted: false,
    });
    s.on('accepted', () => setCall((c) => c && { ...c, state: 'answered', answeredAt: Date.now() }));
    s.on('confirmed', () => {
      attachMedia(s);
      setCall((c) => c && { ...c, state: 'answered', answeredAt: c.answeredAt ?? Date.now() });
    });
    s.on('hold', () => setCall((c) => c && { ...c, state: 'held' }));
    s.on('unhold', () => setCall((c) => c && { ...c, state: 'answered' }));
    let answered = false;
    s.on('confirmed', () => { answered = true; });
    const gone = (e?: { cause?: string }): void => {
      session.current = null;
      setCall(null);
      setLastEnd({ at: Date.now(), answered, cause: answered ? null : (e?.cause ?? null), attemptId: attemptRef.current });
      attemptRef.current = null;
      onEnded?.();
    };
    s.on('ended', gone);
    s.on('failed', gone);
  }, [attachMedia, onEnded]);

  const signIn = useCallback(async () => {
    setError(null);
    setState('connecting');
    try {
      const r = await api.post<BrowserPhone & { password: string }>(base, {});
      setInfo({ ...r, signed_in: true });
      const socket = new JsSIP.WebSocketInterface(r.ws_url!);
      const agent = new JsSIP.UA({
        sockets: [socket],
        uri: `sip:${r.username}@${r.realm}`,
        authorization_user: r.username,
        password: r.password,
        display_name: r.display_name,
        // Short enough that a laptop waking from sleep is back on the edge quickly, long enough
        // not to be a heartbeat: the edge counts registrations per source like any other phone.
        register_expires: 300,
        session_timers: false,
      });
      agent.on('registered', () => setState('ready'));
      agent.on('unregistered', () => setState('off'));
      agent.on('registrationFailed', (e) => {
        setState('failed');
        setError(`The edge refused this phone (${e.cause}).`);
      });
      agent.on('disconnected', () => setState((s) => (s === 'off' ? s : 'failed')));
      agent.on('newRTCSession', ({ session: s, originator }: RTCSessionEvent) => {
        // One call at a time: a second arriving while one is up is refused rather than
        // silently taking over the audio device.
        if (session.current) { s.terminate({ status_code: 486 }); return; }
        wire(s, originator === 'remote');
      });
      agent.start();
      ua.current = agent;
    } catch (e) {
      setState('failed');
      setError(e instanceof Error ? e.message : 'The phone could not sign in.');
    }
  }, [base, wire]);

  const signOut = useCallback(async () => {
    session.current?.terminate();
    session.current = null;
    ua.current?.stop();
    ua.current = null;
    setState('off');
    setCall(null);
    await api.delete(base).catch(() => undefined);
    setInfo((i) => i && { ...i, signed_in: false });
  }, [base]);

  /**
   * A closed tab gives the credential back.
   *
   * Best effort, deliberately: the request is sent on the way out and the browser may cut it off,
   * so the credential is ALSO replaced by the next sign-in and dropped when the seat's password is
   * reissued. Nothing here is the only thing standing between a closed tab and a live phone --
   * unregistering is, and that happens first.
   */
  useEffect(() => {
    const leave = (): void => {
      if (!ua.current) return;
      ua.current.stop();
      ua.current = null;
      void api.delete(base).catch(() => undefined);
    };
    window.addEventListener('pagehide', leave);
    return () => {
      window.removeEventListener('pagehide', leave);
      leave();
    };
  }, [base]);

  const dial = useCallback((number: string, opts?: { attemptId?: string }) => {
    const agent = ua.current;
    const target = number.trim();
    if (!agent || !target || session.current) return;
    attemptRef.current = opts?.attemptId ?? null;
    agent.call(`sip:${target}@${info?.realm ?? ''}`, {
      // Only a uuid: the dialplan takes nothing else from it and drops it before the carrier.
      ...(opts?.attemptId && /^[0-9a-f-]{36}$/.test(opts.attemptId) ? { extraHeaders: [`X-Arc-Attempt: ${opts.attemptId}`] } : {}),
      mediaConstraints: { audio: true, video: false },
      // The microphone is asked for HERE, on a call, not when the page loads: a permission
      // prompt on arrival is how people end up refusing it for good.
      pcConfig: { rtcpMuxPolicy: 'require' },
    });
  }, [info?.realm]);

  const answer = useCallback(() => {
    session.current?.answer({ mediaConstraints: { audio: true, video: false } });
  }, []);
  const hangUp = useCallback(() => { session.current?.terminate(); }, []);
  const hold = useCallback((on: boolean) => {
    if (on) session.current?.hold(); else session.current?.unhold();
  }, []);
  const mute = useCallback((on: boolean) => {
    if (on) session.current?.mute({ audio: true }); else session.current?.unmute({ audio: true });
    setCall((c) => c && { ...c, muted: on });
  }, []);
  const sendDtmf = useCallback((digit: string) => { session.current?.sendDTMF(digit); }, []);
  const transfer = useCallback((target: string) => {
    if (!target.trim()) return;
    session.current?.refer(`sip:${target.trim()}@${info?.realm ?? ''}`);
  }, [info?.realm]);

  return useMemo(() => ({
    info, state, error, call, lastEnd,
    signIn, signOut, dial, answer, hangUp, hold, mute, sendDtmf, transfer,
  }), [info, state, error, call, lastEnd, signIn, signOut, dial, answer, hangUp, hold, mute, sendDtmf, transfer]);
}
